import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopBar } from "@/components/TopBar";
import { motion } from "framer-motion";
import { ArrowLeft, Award, Lock } from "lucide-react";

export default function BadgesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isParent = user?.role === "Parent";
  const [students, setStudents] = useState([]);
  const [sid, setSid] = useState("");
  const [data, setData] = useState(null);

  const targetId = isParent ? sid : user?.id;
  const load = useCallback(() => { if (targetId) api.get(`/badges/${targetId}`).then((r) => setData(r.data)); }, [targetId]);

  useEffect(() => { if (isParent) api.get("/students").then((r) => { setStudents(r.data); if (r.data[0]) setSid(r.data[0].id); }); }, [isParent]);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <TopBar subtitle="Badges" />
      <main className="max-w-3xl mx-auto px-6 md:px-8 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <button onClick={() => navigate(isParent ? "/parent" : "/student")} className="flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#3D405B]"><ArrowLeft size={16} /> Back</button>
          {isParent && (
            <select data-testid="badges-student" value={sid} onChange={(e) => setSid(e.target.value)}
              className="rounded-full border border-[#EAE6DF] bg-white px-5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#E07A5F]">
              {students.map((s) => <option key={s.id} value={s.id}>{s.first_name}</option>)}
            </select>
          )}
        </div>

        <div className="relative grain rounded-3xl overflow-hidden p-8 text-white mb-8" style={{ background: "#3D405B" }}>
          <div className="flex items-center gap-2 text-white/80"><Award size={16} /><span className="text-xs font-semibold uppercase tracking-[0.2em]">Badge Collection</span></div>
          <h1 className="font-head text-4xl font-bold mt-2">{data ? `${data.earned_count} of ${data.total} earned` : "Badges"}</h1>
          <p className="text-white/80 mt-1">Keep showing up and doing your work to unlock them all!</p>
        </div>

        {data && (
          <div data-testid="badge-grid" className="grid grid-cols-2 sm:grid-cols-3 gap-5">
            {data.badges.map((b, i) => (
              <motion.div key={b.id} data-testid={`badge-${b.id}`}
                initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.05 }}
                className={`rounded-2xl border p-5 text-center ${b.earned ? "bg-white border-[#EAE6DF]" : "bg-[#F2EFE9] border-[#EAE6DF]"}`}>
                <div className="relative w-24 h-24 mx-auto">
                  <img src={b.icon} alt={b.name} className={`w-full h-full object-contain transition-all ${b.earned ? "" : "grayscale opacity-40"}`} />
                  {!b.earned && <span className="absolute inset-0 grid place-items-center"><Lock size={20} className="text-[#6B7280]" /></span>}
                </div>
                <p className={`font-head font-semibold mt-2 ${b.earned ? "text-[#3D405B]" : "text-[#6B7280]"}`}>{b.name}</p>
                <p className="text-xs text-[#6B7280] mt-0.5">{b.desc}</p>
                {!b.earned && b.goal > 1 && (
                  <div className="mt-2 h-1.5 rounded-full bg-[#EAE6DF] overflow-hidden">
                    <div className="h-full bg-[#81B29A] transition-all" style={{ width: `${(b.current / b.goal) * 100}%` }} />
                  </div>
                )}
                {b.earned && <p className="text-xs font-semibold text-[#81B29A] mt-1">Earned!</p>}
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
